<template>
  <div :class="['header-container', { 'header-displaced': !['Login', 'TokenExpired', 'NotFound'].includes($route.name) }]">
    <!-- Primera columna: Logotipo -->
    <div class="header-logo">
      <div class="logo-container">
        <img class="logo" :src="logo" alt="Logotipo de la empresa" />
      </div>
    </div>

    <!-- Segunda columna: Título -->
    <div class="header-title">
      <h1 v-fit-text>{{ $t('gestion_tickets') }}</h1>
      <div class="icon-row-horizontal" v-if="!['Login', 'TokenExpired', 'NotFound'].includes($route.name)">
        <div class="go-to-welcome" v-if="$route.name !== 'Welcome'" @click="goToWelcome">
          <span class="material-icons go-to-welcome-icon" :title="$t('gestion_tickets')">support_agent</span>
        </div>
        <div class="manage-users" v-if="userRole === 1 && $route.name !== 'UserManagement'">
          <span class="material-icons manage-users-icon" @click="goToUserManagement" :title="$t('gestion_usuarios')">manage_accounts</span>
        </div>
        <div class="view-logs" v-if="$route.name === 'UserManagement'" @click="openLogsModal">
          <span class="material-icons view-logs-icon" :title="$t('ver_logs_todos_usuarios')">list_alt</span>
        </div>
        <div class="table-maintenance" v-if="userRole === 1 && $route.name !== 'TableManagement'" @click="goToTableMaintenance">
          <span class="material-icons table-maintenance-icon" :title="$t('mantenimiento_tablas')">settings</span>
        </div>
        <div class="back-button" v-if="$route.name !== 'Welcome'" @click="goBack">
          <span class="material-icons" :title="$t('volver')">arrow_back</span>
        </div>

        <!-- Icono de ayuda movido a la columna central; será el más a la derecha -->
        <router-link to="/manual" class="manual-icon" :title="$t('manual.titulo')">
          <span class="material-icons">help</span>
        </router-link>
      </div>
      <!-- Selector de idioma movido a la columna de acciones de usuario, entre logout e nombre -->
    </div>

    <!-- Tercera columna: Acciones de usuario a la derecha -->
    <div class="header-user-actions" v-if="!['TokenExpired', 'NotFound'].includes($route.name)">
      <!-- Elimina el v-if="$route.name !== 'Login'" para mostrar siempre el selector de idioma -->
      <div>
        <div class="logout-row" v-if="$route.name !== 'Login'">
          <button class="logout-button" @click="handleLogout">
            <i class="material-icons">logout</i> {{ $t('cerrar_sesion') }}
          </button>
        </div>
        <!-- Selector de idioma SIEMPRE visible -->
        <div class="language-selector-inline">
          <div class="custom-lang-select" @click="toggleLangDropdown">
            <img :src="getFlag(selectedLocale)" :alt="selectedLocale" class="flag-in-select" />
            <span class="lang-label">{{ getLangLabel(selectedLocale) }}</span>
            <span class="arrow" :class="{open: langDropdownOpen}">▼</span>
            <ul v-show="langDropdownOpen" class="lang-dropdown">
              <li v-for="lang in languages" :key="lang.value" @click.stop="selectLang(lang.value)">
                <img :src="lang.flag" :alt="lang.value" class="flag-in-select" />
                <span>{{ lang.label }}</span>
              </li>
            </ul>
          </div>
        </div>
        <!-- Nombre de usuario y ayuda solo si no es login -->
        <div class="user-info-row" v-if="$route.name !== 'Login'">
          <div class="user-name" :title="userStore.name">
            {{ userName }}
            <span class="material-icons key-icon" :title="$t('cambiar_contrasena')" style="color:#99172A;cursor:pointer;vertical-align:middle;margin-left:8px;" @click="openChangePasswordModal()">vpn_key</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <UserLogModal
    :visible="showLogsModal"
    :logs="logs"
    :title="$t('logs_todos_usuarios')"
    @close="showLogsModal = false"
  />

  <!-- Modal para cambiar la contraseña de un usuario -->
  <teleport to="body">
    <div v-if="showChangePasswordModal" class="chgpass-wrapper">
      <div class="modal-backdrop" @click="closeChangePasswordModal"></div>
      <div class="modal-content chgpass-modal" role="dialog" aria-modal="true">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ $t('cambiar_contrasena') }}<br />
            <span class="user-name">{{ selectedUserName }}</span>
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="closeChangePasswordModal"
            aria-label="Close"
            title="Cerrar"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleChangePassword">
            <div class="mb-3">
              <label for="old_password" class="form-label">{{$t('contrasena_actual')}}</label>
              <div class="password-container">
                <input
                  :type="showCurrentPassword ? 'text' : 'password'"
                  id="old_password"
                  v-model="passwordForm.old_password"
                  class="form-input"
                  required
                />
                <span
                  class="material-icons password-toggle"
                  @click="toggleShowCurrentPassword"
                  style="color: #99172A; cursor: pointer;"
                >
                  {{ showCurrentPassword ? 'visibility_off' : 'visibility' }}
                </span>
              </div>
            </div>
            <div class="mb-3">
              <label for="new_password" class="form-label">{{ $t('nueva_contrasena') }}</label>
              <div class="password-container">
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  id="new_password"
                  v-model="passwordForm.new_password"
                  class="form-input"
                  required
                />
                <span
                  class="material-icons password-toggle"
                  @click="toggleShowNewPassword"
                  style="color: #99172A; cursor: pointer;"
                >
                  {{ showNewPassword ? 'visibility_off' : 'visibility' }}
                </span>
              </div>
            </div>
            <div class="mb-3">
              <label for="new_password_confirmation" class="form-label">{{ $t('repetir_contraseña') }}</label>
              <div class="password-container">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  id="new_password_confirmation"
                  v-model="passwordForm.new_password_confirmation"
                  class="form-input"
                  required
                />
                <span
                  class="material-icons password-toggle"
                  @click="toggleShowConfirmPassword"
                  style="color: #99172A; cursor: pointer;"
                >
                  {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
                </span>
              </div>
            </div>
            <div class="modal-footer">
              <button type="submit" class="btn btn-primary" :disabled="isSaving">
                <span v-if="isSaving">{{ $t('guardando') }}…</span>
                <span v-else>{{ $t('guardar') }}</span>
              </button>
              <button type="button" class="btn btn-secondary" @click="closeChangePasswordModal">{{ $t('cancelar') }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import logo from '../assets/logo.svg';
import api from '@/api.js';
import UserLogModal from '@/views/UserLogModal.vue';
import { useUserStore } from '@/stores/user';
import Swal from 'sweetalert2';

export default {
  components: {
    UserLogModal,
  },
  directives: {
    // Ajusta el tamaño de fuente para que el texto quepa en una sola línea
    fitText: {
      mounted(el) {
        el.style.whiteSpace = 'nowrap';
        el.style.overflow = 'hidden';
        el.style.textOverflow = 'clip';
        el.style.display = 'block';

        const computeAdjust = () => {
          const computed = window.getComputedStyle(el);
          const maxFont = parseFloat(computed.fontSize) || 20;
          let fontSize = maxFont;
          el.style.fontSize = fontSize + 'px';
          // reducir hasta que quepa — límite mínimo 10px
          while (el.scrollWidth > el.clientWidth && fontSize > 10) {
            fontSize -= 1;
            el.style.fontSize = fontSize + 'px';
          }
          // intentar volver a aumentar si hay espacio y no supera el original
          while (el.scrollWidth <= el.clientWidth && fontSize < maxFont) {
            fontSize += 1;
            el.style.fontSize = fontSize + 'px';
            if (el.scrollWidth > el.clientWidth) {
              fontSize -= 1;
              el.style.fontSize = fontSize + 'px';
              break;
            }
          }
        };

        // guardar referencias para cleanup
        el.__fitText = { adjust: computeAdjust };
        // Observador de resize + fallback window.resize
        if ('ResizeObserver' in window) {
          el.__fitText.ro = new ResizeObserver(() => computeAdjust());
          el.__fitText.ro.observe(el);
        }
        window.addEventListener('resize', computeAdjust);
        // disparar ajuste inicial y también al poco tiempo para textos async
        computeAdjust();
        setTimeout(computeAdjust, 100);
      },
      updated(el) {
        el.__fitText?.adjust?.();
      },
      unmounted(el) {
        if (el.__fitText?.ro) {
          try { el.__fitText.ro.disconnect(); } catch (e) {}
        }
        window.removeEventListener('resize', el.__fitText?.adjust);
        delete el.__fitText;
      }
    }
  },
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  data() {
    return {
      logo,
      logs: [],
      showLogsModal: false,
      selectedLocale: localStorage.getItem('selectedLocale') || this.$i18n.locale,
      languages: [
        { value: 'es', label: 'Español', flag: '/img/flags/es.webp' },
        { value: 'en', label: 'English', flag: '/img/flags/gb.webp' },
        { value: 'fr', label: 'Français', flag: '/img/flags/fr.webp' },
        { value: 'it', label: 'Italiano', flag: '/img/flags/it.webp' },
        { value: 'pt', label: 'Português', flag: '/img/flags/pt.webp' },
        { value: 'ro', label: 'Română', flag: '/img/flags/ro.webp' },
      ],
      langDropdownOpen: false,
      isMobile: window.innerWidth <= 600,
      // Modal cambio contraseña
      showChangePasswordModal: false,
      showNewPassword: false,
      showConfirmPassword: false,
      showCurrentPassword: false,
      selectedUserName: '',
      selectedUserId: null,
      passwordForm: {
        old_password: '',
        new_password: '',
        new_password_confirmation: '',
        forzar_cambio_contrasena: true,
      },
      // flag para bloquear submit de cambio de contraseña
      isSaving: false,
    };
  },
  computed: {
    userName() {
      const name = this.userStore.name || '';
      if (name.length > 30) {
        return name.slice(0, 27) + '...';
      }
      return name;
    },
    userRole() {
      return this.userStore.role;
    },
  },
  watch: {
    showChangePasswordModal(val) {
      // evitar scroll del body mientras el overlay esté activo
      document.body.style.overflow = val ? 'hidden' : '';
      // forzar que el body actúe como contenedor para posicion:absolute del overlay
      document.body.style.position = val ? 'relative' : '';
      // si se abre, forzar re-render para que modalContainerStyle recalcule la altura
      if (val) {
        this.$nextTick(() => { this.$forceUpdate(); });
        // actualizar también al redimensionar/scroll para mantener altura correcta
        window.addEventListener('resize', this._updateModalOverlayHeight);
        window.addEventListener('scroll', this._updateModalOverlayHeight);
      } else {
        window.removeEventListener('resize', this._updateModalOverlayHeight);
        window.removeEventListener('scroll', this._updateModalOverlayHeight);
      }
    }
  },
  methods: {
    // método auxiliar para forzar recalculo del estilo del overlay
    _updateModalOverlayHeight() {
      this.$forceUpdate();
    },
    openChangePasswordModal() {
      // Siempre usar el id y nombre del usuario logueado
      this.selectedUserId = this.userStore.id;
      this.selectedUserName = this.userStore.name || '';
      this.passwordForm = {
        old_password: '',
        new_password: '',
        new_password_confirmation: ''
      };
      this.showChangePasswordModal = true;
    },
    closeChangePasswordModal() {
      this.showChangePasswordModal = false;
    },
    toggleShowNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },
    toggleShowConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
    toggleShowCurrentPassword() {
      this.showCurrentPassword = !this.showCurrentPassword;
    },
    async handleChangePassword() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        await api.post('/users/change-password', {
          current_password: this.passwordForm.old_password,
          new_password: this.passwordForm.new_password,
          new_password_confirmation: this.passwordForm.new_password_confirmation
        });

        // crear entrada de log (opcional, si el backend no lo crea)
        try {
          await api.post('/logs', {
            accion: 'cambio_contrasena',
            usuario_id: this.userStore.id,
            descripcion: 'Usuario cambió su contraseña',
            fecha: new Date().toISOString()
          });
        } catch (logErr) {
          // no bloquear el flujo por fallo en logging
          console.warn('No se pudo registrar el log de cambio de contraseña:', logErr);
        }

        await Swal.fire({ icon: 'success', title: this.$t('contraseña_actualizada'), confirmButtonColor: '#99172A' });
        this.closeChangePasswordModal();
      } catch (err) {
        const errorMessage = err.response?.data?.message || this.$t('error_cambiar_contrasena');
        await Swal.fire({ icon: 'error', title: 'Error', text: errorMessage, confirmButtonColor: '#99172A' });
      } finally {
        this.isSaving = false;
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 600;
      this.$forceUpdate();
    },
    changeLocale() {
      this.$i18n.locale = this.selectedLocale;
      localStorage.setItem('selectedLocale', this.selectedLocale);
    },
    getFlag(locale) {
      const lang = this.languages.find(l => l.value === locale);
      return lang ? lang.flag : '';
    },
    getLangLabel(locale) {
      const lang = this.languages.find(l => l.value === locale);
      return lang ? lang.label : locale;
    },
    toggleLangDropdown() {
      this.langDropdownOpen = !this.langDropdownOpen;
    },
    selectLang(locale) {
      this.selectedLocale = locale;
      this.changeLocale();
      this.langDropdownOpen = false;
    },
    async openLogsModal() {
      try {
        const response = await api.get('/logs');
        this.logs = response.data.logs || [];
        this.showLogsModal = true;
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: this.$t('error_cargar_logs'), timer: 2500, showConfirmButton: false });
      }
    },
    goToWelcome() {
      this.$router.push({ name: 'Welcome' });
    },
    goToUserManagement() {
      this.$router.push({ name: 'UserManagement' });
    },
    goToTableMaintenance() {
      this.$router.push({ name: 'TableManagement' });
    },
    goBack() {
      this.$router.go(-1);
    },
    async handleLogout() {
      try {
        await api.post('/logout');
      } catch (error) {
        // opcional: mostrar error solo en DEV
        if (import.meta.env.DEV) console.error('Logout error:', error);
      } finally {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_name');
        localStorage.removeItem('user_role');
        this.userStore.clearUser();
        this.$router.push({ name: 'Login' });
      }
    },
  },
};
</script>

<style scoped>
.btn-close {
  filter: invert(1);
  opacity: 0.8;
  color: white;
}

.btn-close:hover {
  opacity: 1;
}

/* asegurar single-line por defecto (directiva hace el ajuste de font-size) */
.header-title h1 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
  margin: 0;
  line-height: 1;
  /* tamaño base; la directiva lo ajustará hacia abajo si hace falta */
  font-size: 24px;
}

.custom-lang-select {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: #798a99;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 6px 12px;
  min-width: unset;
  max-width: 200px;
  width: auto;
  cursor: pointer;
  user-select: none;
  height: 38px;
  font-size: 15px;
  font-family: inherit;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  transition: background 0.2s, color 0.2s;
  z-index: 20;
}

.custom-lang-select .flag-in-select {
  width: 22px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  margin-right: 8px;
}

.custom-lang-select .lang-label {
  margin-right: 8px;
  font-family: inherit;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.01em;
}

.custom-lang-select .arrow {
  font-size: 0.9em;
  margin-left: 2px;
  color: #fff;
  transition: transform 0.2s;
}

.custom-lang-select .arrow.open {
  transform: rotate(180deg);
}

.lang-dropdown {
  position: absolute;
  top: 110%;
  left: 0;
  background: #798a99;
  color: #fff;
  border: none;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  min-width: 140px;
  padding: 0.2em 0;
  z-index: 100;
  list-style: none;
}

.lang-dropdown li {
  display: flex;
  align-items: center;
  padding: 0.4em 1em 0.4em 0.5em;
  cursor: pointer;
  font-size: 15px;
  font-family: inherit;
  font-weight: bold;
  color: #fff;
  background: transparent;
  transition: background 0.15s, color 0.15s;
  border-radius: 5px;
}

.lang-dropdown li:hover {
  background: #fff;
  color: #798a99;
}

.lang-dropdown .flag-in-select {
  margin-right: 8px;
}

/* Segunda columna: Título */
.header-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  color: #000;
  text-align: center;
  padding: 0;
  box-sizing: border-box;
  min-height: 48px;
  height: auto;
  flex: 0 0 50%;
  max-width: 50%;
}

/* Contenedor principal del encabezado: centrar horizontalmente y mantener responsivo */
.header-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0;
  background-color: #fff;
  color: #333;
  padding: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-wrap: nowrap;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-bottom: 3px solid #99172A;
}

/* Ajuste de columnas: repartir espacio pero centrar su contenido */
.header-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 25%;
  min-width: 80px;
}

.header-title {
  flex: 1 1 50%;
  max-width: 60%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0;
  box-sizing: border-box;
}

.header-user-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  gap: 8px;
  min-width: 120px;
  padding-right: 16px;
  box-sizing: border-box;
}

/* versión columna (segunda definición) */
.header-user-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  flex: 0 0 25%;
  max-width: 25%;
  min-width: 120px;
  gap: 5px;
  padding-right: 16px;
  box-sizing: border-box;
}

/* Móvil: mantener algo de margen lateral */
@media (max-width: 600px) {
  .header-user-actions {
    padding-right: 12px;
  }
}

/* Iconos en una sola fila debajo del título */
.icon-row-horizontal {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-top: 0;
}

.icon-row-horizontal .material-icons,
.icon-row-horizontal .material-icons-outlined,
.icon-row-horizontal .material-icons-round {
  color: #99172A !important;
  font-size: 28px;
  vertical-align: middle;
  transition: color 0.2s;
  cursor: pointer;
}

.icon-row-horizontal .material-icons:hover,
.icon-row-horizontal .material-icons-outlined:hover,
.icon-row-horizontal .material-icons-round:hover {
  color: #7A1222 !important;
}

/* Tercera columna: Acciones de usuario a la derecha */
.header-user-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  flex: 0 0 25%;
  max-width: 25%;
  min-width: 120px;
  gap: 5px;
}

.logout-row {
  display: flex;
  justify-content: flex-end;
}

.logout-button {
  background-color: #798a99;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 6px 12px;
  font-size: 15px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  width: 170px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 38px;
  min-height: unset;
  box-sizing: border-box;
}

.logout-button:hover {
  background-color: #5e6e7a;
}

.language-selector-inline {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding-right: 0;
  margin: 5px 0;
}

#language-select {
  padding: 6px 12px;
  font-size: 14px;
  color: #17a2b8 !important;
  background-color: #798a99;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  outline: none;
}

/* Asegurar visualización del logotipo */
.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  z-index: 110;
}

.logo {
  display: block !important;
  width: 160px;
  height: auto !important;
  vertical-align: middle;
  user-select: none;
}

/* Reducir tamaño en móvil */
@media (max-width: 600px) {
  .logo-container { width: 90px; min-width: 56px; }
  .logo { width: 90px; max-height: 48px; }
}

/* Evitar que reglas invisibilicen la imagen */
.logo[hidden],
.logo[style*="display: none"],
.logo[style*="visibility: hidden"] {
  display: block !important;
  visibility: visible !important;
}

/* ---------- ESTILOS PARA EL MODAL DE CAMBIO DE CONTRASEÑA ---------- */
/* Backdrop para el modal de cambio de contraseña */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9000;
  pointer-events: auto;
}

/* Forzar que la variante del modal de cambio de contraseña use posicionamiento fijo y se centre */
.chgpass-modal.modal-content,
.chgpass-modal {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 10000 !important;
  margin: 0 !important;
  width: 100%;
  max-width: 500px;
  background: #ffffff !important;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(153,19,19,0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Caja del modal: permitir scroll interno y limitar altura */
.modal-content {
  width: 100%;
  background: #ffffff !important;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(153,19,19,0.25);
  overflow: hidden;
  box-sizing: border-box;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

/* Cabecera y cuerpo: el cuerpo puede hacer scroll si es necesario */
.modal-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  flex: 0 0 auto;
  top: 0;
  left: 0;
  border-radius: 8px;
  background-color: #99172A;
  color: white;
}

.modal-body {
  padding: 12px 16px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 120px;
}

.modal-footer {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* Ajustes para pantallas pequeñas */
@media (max-width: 600px) {
  .modal-content {
    max-width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 4px;
  }
  
  .chgpass-modal.modal-content,
  .chgpass-modal {
    max-width: 95%;
  }
  
  .modal-header, .modal-body, .modal-footer { 
    padding-left: 12px; 
    padding-right: 12px; 
  }
}

/* Reglas para pantallas pequeñas: apilar y centrar todo */
@media (max-width: 600px) {
  .header-container {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    padding: 0;
  }
  
  .header-logo, .header-title, .header-user-actions {
    width: 100%;
    max-width: 100%;
    flex: 0 0 auto;
    justify-content: center !important;
    align-items: center !important;
    text-align: center;
    margin: 0;
  }

  /* Ajustes: centrar y separar cuando quedan en columna */
  .header-user-actions {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 10px;
    padding-right: 12px;
  }

  .logout-row {
    width: 100%;
    display: flex;
    justify-content: center;
    margin: 0;
  }

  .language-selector-inline {
    order: 2;
    width: 100%;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* hacer que los controles tengan margen horizontal automático y no toquen los bordes */
  .custom-lang-select,
  .logout-button {
    width: calc(100% - 32px) !important;
    max-width: 420px;
    margin: 0 auto;
  }

  .icon-row-horizontal {
    order: 1;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .user-name {
    width: 100%;
    text-align: center;
    font-size: 13px;
  }
}

/* Estilos para inputs de contraseña */
.password-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-container .form-input {
  flex: 1;
  padding-right: 40px;
}

.password-container .password-toggle {
  position: absolute;
  right: 10px;
  cursor: pointer;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #99172A;
  box-shadow: 0 0 0 3px rgba(153,23,42,0.1);
}

.btn-primary {
  background-color: #99172A;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background-color: #7a111f;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-secondary:hover {
  background-color: #5a6268;
}
</style>

<!-- Estilos GLOBALES para modal/overlay (no scoped) -->
<style>
/* Overlay/backdrop global: ocupa todo el viewport */
.chgpass-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Asegura que el modal esté por encima del overlay y tenga scroll interno */
.chgpass-modal.modal-content {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 10000 !important;
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 40px);
  background: #ffffff !important;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
}

/* Si el contenido es alto, permitir scroll dentro del body y del modal */
.modal-body {
  overflow-y: auto !important;
  flex: 1 1 auto !important;
}

/* Variante para pantallas pequeñas */
@media (max-width: 600px) {
  .chgpass-modal.modal-content { 
    max-width: 95%; 
    max-height: calc(100vh - 24px); 
    border-radius: 6px; 
  }
}
</style>